import React, { useEffect, useState } from 'react';
import { Download, Monitor, Loader2, AlertCircle, Lock } from 'lucide-react';
import { apiFetch, ApiError } from '../services/apiClient';

interface InstallerInfo {
  exe_url: string;
  exe_name: string;
  size: number | null;
}

interface LatestRelease {
  version: string;
  notes: string;
  pub_date: string | null;
  installer: InstallerInfo;
}

// unauthorized: 설치파일은 사내 계정으로 로그인해야 받을 수 있다.
// 토큰 유효성을 여기서 흉내내지 않고 서버(401)를 단일 판정 출처로 쓴다.
type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; data: LatestRelease }
  | { status: 'unauthorized' }
  | { status: 'unavailable' };

function formatSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return '';
  const mb = bytes / 1024 / 1024;
  return `${mb.toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  // pub_date 예: "2026-05-27T00:00:00Z" → "2026-05-27"
  return iso.split('T')[0];
}

export const DownloadPage: React.FC = () => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<LatestRelease>('/updates/latest');
        if (!cancelled) {
          if (data && data.installer && data.installer.exe_url) {
            setState({ status: 'ready', data });
          } else {
            setState({ status: 'unavailable' });
          }
        }
      } catch (e) {
        if (cancelled) return;
        const needsLogin = e instanceof ApiError && e.status === 401;
        setState({ status: needsLogin ? 'unauthorized' : 'unavailable' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* 헤더 */}
        <div className="p-8 bg-slate-50 border-b border-slate-100 text-center">
          <img src="/icon.png" alt="INA-TALK" className="h-16 w-16 rounded-2xl mx-auto mb-4 shadow-lg" />
          <h1 className="text-2xl font-bold text-slate-800">INA-TALK</h1>
          <p className="mt-1 text-sm text-slate-500 flex items-center justify-center gap-1.5">
            <Monitor className="h-4 w-4" />
            PC 버전 다운로드
          </p>
        </div>

        {/* 본문 */}
        <div className="p-8">
          {state.status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <Loader2 className="animate-spin h-8 w-8 mb-3" />
              <p className="text-sm">최신 버전 정보를 불러오는 중...</p>
            </div>
          )}

          {state.status === 'unauthorized' && (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
              <Lock className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-700">로그인이 필요합니다</p>
                <p className="text-sm text-slate-500">
                  PC 앱 설치 파일은 인트라넷 자료실에서 내려받을 수 있습니다
                </p>
                {/* 기기 신뢰 정책이 켜지면 웹 브라우저 로그인이 막혀, 예전 안내(웹 로그인 → 프로필 사진 → PC 버전 다운로드)는
                    따라 할 수 없는 길이 된다. 배포 경로는 인트라넷 자료실 셀프 설치다(2026-10-29) */}
                <p className="text-xs text-slate-400">
                  정책이 적용되기 전이라면 PC의 웹 브라우저로 로그인한 뒤 왼쪽 아래 프로필 사진 &rarr; PC 버전 다운로드에서도 받을 수 있습니다
                </p>
              </div>
              <a
                href="/"
                className="w-full flex items-center justify-center py-3 px-4 rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-400 transition-all"
              >
                로그인
              </a>
            </div>
          )}

          {state.status === 'unavailable' && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-10 w-10 text-amber-500 mb-3" />
              <p className="text-sm text-slate-600">
                현재 내려받을 수 있는 설치파일이 없습니다.
                <br />
                잠시 후 다시 시도하거나 관리자에게 문의해주세요.
              </p>
            </div>
          )}

          {state.status === 'ready' && (
            <div className="space-y-6">
              <div className="text-center space-y-1">
                <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold">
                  최신 버전 v{state.data.version}
                </div>
                {formatDate(state.data.pub_date) && (
                  <p className="text-xs text-slate-400">
                    배포일 {formatDate(state.data.pub_date)}
                  </p>
                )}
              </div>

              {state.data.notes && (
                <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                  <p className="text-xs font-medium text-slate-500 mb-1">업데이트 내용</p>
                  <p className="text-sm text-slate-700 whitespace-pre-line">
                    {state.data.notes}
                  </p>
                </div>
              )}

              <a
                href={state.data.installer.exe_url}
                download
                className="w-full flex items-center justify-center py-3 px-4 rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-400 transition-all"
              >
                <Download className="-ml-1 mr-2 h-4 w-4" />
                Windows 설치파일 다운로드
                {formatSize(state.data.installer.size) && (
                  <span className="ml-1.5 text-white/80 text-xs">
                    ({formatSize(state.data.installer.size)})
                  </span>
                )}
              </a>

              <p className="text-xs text-slate-400 text-center">
                Windows 10 / 11 (64-bit) 지원
              </p>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-center">
          <a href="/" className="text-xs text-slate-500 hover:text-brand-700 transition-colors">
            웹 버전으로 이동
          </a>
        </div>
      </div>
    </div>
  );
};
